import { type DefaultEdgeOptions, MarkerType } from '@xyflow/react';
import type { NodeTypesKey } from './playbook-builder.types';

export const EDIT_QUERY_KEY = 'edit';
export const ACTIVE_NODE_QUERY_KEY = 'node';

export const BO_KYC_PARENT_NODE_EXTRA_WIDTH = 60;
export const BO_KYC_PARENT_NODE_EXTRA_HEIGHT = 120;
export const BO_KYC_PARENT_NODE_HEADER_HEIGHT = 20;
export const ROOT_NODE_OFFSET = -32;
export const BRANCH_EDGE_LABEL_OFFSET = 30;
export const LAYOUT_X_OFFSET_FACTOR = 0.06;
export const LAYOUT_Y_OFFSET_FACTOR = 0.37;

export const GRAPH_ROW_SPACING = 64;
export const GRAPH_COLUMN_SPACING = 84;

/** This class is attached to edges that are highlighted and should have their z-index lifted. Unfortunately, it's really hard to do this for custom edges in react-flow, so we do it in custom CSS */
export const HIGHLIGHTED_EDGE_CLASS_NAME = 'has-highlight-lift-z-index';

// BO_KYC_NODE_Z_INDEX is a background for its subgraph's edges and nodes.
export const BO_KYC_NODE_Z_INDEX = 1;
export const EDGE_Z_INDEX = 2;
export const HIGHLIGHTED_EDGE_Z_INDEX = 3;
export const EDGE_LABEL_AND_NODE_Z_INDEX = 4;

export const defaultEdgeOptions: DefaultEdgeOptions = {
  selectable: false,
  style: {
    stroke: '#D4D4D4',
  },
  markerEnd: {
    height: 16,
    width: 16,
    type: MarkerType.ArrowClosed,
  },
};

export const nodeMeasurements: Record<NodeTypesKey, { width: number; height: number }> = {
  collect_data: { width: 228, height: 70 },
  collect_custom_data: { width: 244, height: 70 },
  collect_document: { width: 246, height: 70 },
  identity_document: { width: 246, height: 70 },
  selfie: { width: 246, height: 70 },
  custom_document: { width: 232, height: 70 },
  verification_check: { width: 234, height: 70 },
  verified_prefill: { width: 234, height: 70 },
  taktile: { width: 300, height: 70 },
  action: { width: 262, height: 70 },
  conditional: { width: 189, height: 70 },
  rules: { width: 189, height: 70 },
  // This will be overwritten after we measure the actual dimensions of the bo kyc subgraph
  bo_kyc: { width: 966, height: 790 },
  investor_profile: { width: 246, height: 70 },
  input_schema: { width: 246, height: 70 },
  prerequisite: { width: 246, height: 70 },
  output: { width: 246, height: 70 },
  complete: { width: 246, height: 70 },
  identity_data: { width: 246, height: 70 },
  business_data: { width: 246, height: 70 },
  external_api: { width: 246, height: 70 },
  vendor_integration: { width: 246, height: 70 },
  authentication: { width: 246, height: 70 },
  branch: { width: 246, height: 70 },
  bank_link: { width: 246, height: 70 },
  collect_card_data: { width: 246, height: 70 },
  placeholder: { width: 28, height: 28 },
  join_point: { width: 28, height: 28 },
  edge_label: { width: 180, height: 28 },
  assign_data: { width: 246, height: 70 },
  common_expression: { width: 246, height: 70 },
  code_exec: { width: 246, height: 70 },
  watchlist_review: { width: 300, height: 70 },
  document_extraction: { width: 300, height: 70 },
  ai_research: { width: 300, height: 70 },
  ai_decision: { width: 300, height: 70 },
};

/** Hardcoded list of tailwind CSS classes to set the minimum width of each node kind. Keep this in sync with `nodeMeasurements` above. */
export const nodeMeasurementWidths: Record<NodeTypesKey, `min-w-[${number}px]`> = {
  collect_data: 'min-w-[228px]',
  collect_custom_data: 'min-w-[244px]',
  collect_document: 'min-w-[246px]',
  identity_document: 'min-w-[246px]',
  selfie: 'min-w-[246px]',
  custom_document: 'min-w-[232px]',
  verification_check: 'min-w-[234px]',
  verified_prefill: 'min-w-[234px]',
  taktile: 'min-w-[300px]',
  action: 'min-w-[262px]',
  conditional: 'min-w-[189px]',
  rules: 'min-w-[189px]',
  bo_kyc: 'min-w-[966px]',
  investor_profile: 'min-w-[246px]',
  input_schema: 'min-w-[246px]',
  prerequisite: 'min-w-[246px]',
  output: 'min-w-[246px]',
  complete: 'min-w-[246px]',
  identity_data: 'min-w-[246px]',
  business_data: 'min-w-[246px]',
  external_api: 'min-w-[246px]',
  vendor_integration: 'min-w-[246px]',
  authentication: 'min-w-[246px]',
  branch: 'min-w-[246px]',
  bank_link: 'min-w-[246px]',
  collect_card_data: 'min-w-[246px]',
  placeholder: 'min-w-[28px]',
  join_point: 'min-w-[28px]',
  edge_label: 'min-w-[180px]',
  assign_data: 'min-w-[246px]',
  common_expression: 'min-w-[246px]',
  code_exec: 'min-w-[246px]',
  watchlist_review: 'min-w-[300px]',
  document_extraction: 'min-w-[300px]',
  ai_research: 'min-w-[300px]',
  ai_decision: 'min-w-[300px]',
  // person_adverse_media_review: 'min-w-[262px]',
};
